import {
  VentureInvestmentMethodCreateDocument,
  VentureInvestmentMethodDeleteDocument,
  VentureInvestmentMethodGetByIdDocument,
  VentureInvestmentMethodListDocument,
  VentureInvestmentMethodUpdateDocument,
  VentureInvestmentMethodsPaginatedDocument,
} from "api/generated";
import { RequestTypes } from "api/services/enums";
import { getRequestInstance } from "api/services/utils";
import { PartialNullable } from "config";

import {
  IInvestmentMethodCreateVars,
  IInvestmentMethodModel,
  IInvestmentMethodPaginatedResponse,
  IInvestmentMethodUpdateVars,
  IInvestmentMethodsListVars,
  IInvestmentMethodsPaginatedVars,
  InvestmentMethodsListResponse,
} from "./types";
import { client } from "config/apollo";
import { executeEviction } from "api/utils/cache-eviction";

export async function getInvestmentMethodsPaginated(
  variables: IInvestmentMethodsPaginatedVars
): Promise<IInvestmentMethodPaginatedResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: VentureInvestmentMethodsPaginatedDocument,
      variables,
      fetchPolicy: "cache-first",
    });

    return res?.data?.ventureInvestmentMethodsPaginated;
  } catch (error) {
    throw error;
  }
}

export async function createInvestmentMethod(
  variables: IInvestmentMethodCreateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: VentureInvestmentMethodCreateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "VentureInvestmentMethod",
      fieldNames: [
        "ventureInvestmentMethodsPaginated",
        "ventureInvestmentMethodList",
      ],
    });

    return res?.data?.ventureInvestmentMethodCreate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function updateInvestmentMethod(
  variables: IInvestmentMethodUpdateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: VentureInvestmentMethodUpdateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "VentureInvestmentMethod",
      id: variables.ventureInvestmentMethodId.toString(),
      fieldNames: [
        "ventureInvestmentMethodsPaginated",
        "ventureInvestmentMethodList",
        "ventureInvestmentMethodGetById",
      ],
    });

    return res?.data?.ventureInvestmentMethodUpdate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function getInvestmentMethod(
  ventureInvestmentMethodId: number
): Promise<PartialNullable<IInvestmentMethodModel>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: VentureInvestmentMethodGetByIdDocument,
      variables: {
        ventureInvestmentMethodId,
      },
      fetchPolicy: "cache-first",
    });

    return res?.data?.ventureInvestmentMethodGetById;
  } catch (error) {
    throw error;
  }
}

export async function deleteInvestmentMethod(
  ventureInvestmentMethodId: number
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: VentureInvestmentMethodDeleteDocument,
      variables: {
        ventureInvestmentMethodId,
      },
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "VentureInvestmentMethod",
      id: ventureInvestmentMethodId.toString(),
      fieldNames: [
        "ventureInvestmentMethodsPaginated",
        "ventureInvestmentMethodList",
        "ventureInvestmentMethodGetById",
      ],
    });

    return Boolean(res?.data?.ventureInvestmentMethodDelete);
  } catch (error) {
    throw error;
  }
}

export async function getInvestmentMethods(
  variables: IInvestmentMethodsListVars
): Promise<InvestmentMethodsListResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: VentureInvestmentMethodListDocument,
      variables,
      fetchPolicy: "cache-first",
    });

    return res?.data?.ventureInvestmentMethodList;
  } catch (error) {
    throw error;
  }
}
